import { DateTime } from 'luxon'
import React, { useMemo, useState } from 'react'

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid'
import { tx } from '@twind/core'

const weeks = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

export type CalendarProps = {
  data?: Record<string, number>
  activeBackgroundColor?: string
  className?: string
  isWeekWise?: boolean
}

const Calendar = (props: CalendarProps) => {
  const { data, activeBackgroundColor, className, isWeekWise } = props
  const [dt, setDt] = useState(DateTime.now())

  const previous = () => {
    setDt((current) => current.minus({ [isWeekWise ? 'weeks' : 'months']: 1 }))
  }

  const next = () => {
    setDt((current) => current.plus({ [isWeekWise ? 'weeks' : 'months']: 1 }))
  }

  return (
    <div className={tx('flex flex-col space-y-3', className)}>
      <div className="flex justify-center items-center space-x-3">
        <span>
          <ChevronLeftIcon
            className="h-5 w-5 cursor-pointer text-primary"
            onClick={() => previous()}
          />
        </span>
        <div className="text-lg whitespace-nowrap">
          {`${dt.get('monthShort')} ${dt.get('year')}`}
        </div>
        <span>
          <ChevronRightIcon
            className="h-5 w-5 cursor-pointer text-primary"
            onClick={() => next()}
          />
        </span>
      </div>
      <div className="grid grid-cols-7">
        {weeks.map((day, index) => (
          <span className="text-content-dark text-center" key={index}>
            {day}
          </span>
        ))}
        <Days
          dt={dt}
          data={data}
          activeBackgroundColor={activeBackgroundColor}
          isWeekWise={isWeekWise}
        />
      </div>
    </div>
  )
}

const Days = ({
  dt,
  data,
  activeBackgroundColor,
  isWeekWise
}: {
  dt: DateTime
  data?: Record<string, number>
  activeBackgroundColor?: string
  isWeekWise?: boolean
}) => {
  const weekOrMonth = isWeekWise ? 'week' : 'month'
  const days = useMemo(() => {
    const daysArray = []

    const startDay = dt.startOf(weekOrMonth)
    const endDay = dt.endOf(weekOrMonth)
    const diffObject = endDay.diff(startDay, ['days'])
    const diffDays = Math.ceil(diffObject.days)

    for (let index = 0; index < diffDays; index += 1) {
      daysArray.push(dt.startOf(weekOrMonth).plus({ days: index }))
    }
    return daysArray
  }, [weekOrMonth, dt])

  const todayDate = useMemo(() => DateTime.now().toISODate(), [])

  return (
    <>
      {[...Array((dt.startOf(weekOrMonth).get('weekday') + 6) % 7)].map(
        (_, index) => {
          return <span key={`calendar_empty_days_${index}`}></span>
        }
      )}
      {days.map((day, index) => (
        <span
          className={tx(
            'flex justify-center items-center text-zinc-50 my-1',
            // @ts-ignore
            data && data[day.toISODate()] && activeBackgroundColor,
            !activeBackgroundColor &&
              data &&
              // @ts-ignore
              data[day.toISODate()] &&
              'bg-green-600',
            day.day === 1 ||
              // @ts-ignore
              (data && !data[days[index - 1].toISODate()] && 'rounded-l-full'),
            day.day === days.length ||
              // @ts-ignore
              (data && !data[days[index + 1].toISODate()] && 'rounded-r-full')
          )}
          key={`calendar_${day.day}_${index}`}>
          <span
            className={tx(
              'rounded-full px-2 py-1 w-full text-center',
              day.toISODate() === todayDate && 'bg-primary'
            )}>
            {day.day}
          </span>
        </span>
      ))}
    </>
  )
}
export default Calendar
